/** Match Reolink S3 filenames to the selected bridge name (e.g. Escambia). */

const STOP_WORDS = new Set(['bridge', 'bridges', 'the', 'and', 'of', 'for']);

const normalize = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

export const getBridgeNameTokens = (bridgeName) =>
  normalize(bridgeName)
    .split(' ')
    .filter((token) => token.length >= 4 && !STOP_WORDS.has(token));

/** Strongest word in the bridge name — "Escambia Bay Bridge" → "escambia". */
export const getBridgeMatchToken = (bridgeName) => {
  const tokens = getBridgeNameTokens(bridgeName);
  return tokens.find((token) => token.length >= 6) || tokens[0] || '';
};

/** FTP search terms: Destin Bridge → destin, Destin, destin1 */
export const getBridgeFtpSearchTerms = (bridgeName) => {
  const terms = new Set();
  const token = getBridgeMatchToken(bridgeName);
  if (token) {
    terms.add(token);
    terms.add(`${token}1`);
    terms.add(`${token}2`);
  }
  const stripped = normalize(bridgeName).replace(/\bbridges?\b/g, ' ').replace(/\s+/g, ' ').trim();
  if (stripped) {
    terms.add(stripped);
    terms.add(stripped.replace(/\s+/g, ''));
  }
  return [...terms].filter((term) => term && term.length >= 3);
};

const videoSearchText = (video) =>
  normalize([
    video?.fileName,
    video?.s3Key,
    video?.key,
    video?.bridgeName,
    video?.name,
  ].filter(Boolean).join(' '));

const compact = (value) => String(value || '').replace(/\s+/g, '');

export const videoMatchesBridgeName = (video, bridge) => {
  if (!video) return false;
  const bridgeId = String(bridge?._id || bridge?.id || '');
  if (bridgeId && video.bridgeId != null && String(video.bridgeId) === bridgeId) {
    return true;
  }

  const haystack = videoSearchText(video);
  if (!haystack) return false;
  const compactHaystack = compact(haystack);

  const fullName = normalize(bridge?.name);
  if (fullName && (haystack.includes(fullName) || compactHaystack.includes(compact(fullName)))) {
    return true;
  }

  const withoutBridge = fullName.replace(/\bbridges?\b/g, ' ').replace(/\s+/g, ' ').trim();
  if (withoutBridge && (
    haystack.includes(withoutBridge)
    || compactHaystack.includes(compact(withoutBridge))
  )) {
    return true;
  }

  const tokens = getBridgeNameTokens(bridge?.name);
  if (!tokens.length) return true;

  // "Destin Bridge" must match FTP name "Destin1_00_....mp4" (no space before camera no).
  if (tokens.some((token) => haystack.includes(token) || compactHaystack.includes(token))) {
    return true;
  }

  return tokens.every((token) => (
    haystack.includes(token)
    || compactHaystack.includes(token)
    || (token.length >= 4 && compactHaystack.includes(token.slice(0, 4)))
  ));
};

export const filterReolinkVideosByBridge = (videos, bridge) => {
  const list = Array.isArray(videos) ? videos : [];
  if (!bridge?.name && !(bridge?._id || bridge?.id)) return list;
  const matched = list.filter((video) => videoMatchesBridgeName(video, bridge));
  // Bridge-scoped API already limited the set — never wipe Destin/Escambia to [].
  return matched.length ? matched : list;
};

const FIELD_HIGHLIGHT_STOP_WORDS = new Set(['bridge', 'bridges', 'the', 'and', 'of', 'for']);

const fieldHighlightTokens = (value) =>
  normalize(value)
    .split(' ')
    .filter((token) => token.length >= 3 && !FIELD_HIGHLIGHT_STOP_WORDS.has(token));

/** "destin1" → "destin" so glued camera indexes still match the site name. */
const stripCameraIndex = (token) => {
  const stripped = String(token || '').replace(/\d{1,2}$/, '');
  return stripped.length >= 3 ? stripped : token;
};

/**
 * Words before the logger suffix ("9-41") or the camera clock ("1_00_20260919...").
 * "Destin (East Pass) 9-41_00_...." → destin, east, pass
 * "Destin1_00_...." → destin
 */
const fieldHighlightSiteTokens = (fileName) => {
  const base = String(fileName || '').replace(/\.[a-z0-9]+$/i, '').trim();
  if (!base) return [];
  let site = base;
  const loggerCut = base.match(/^(.*?)[\s_]+(\d{1,2}-\d{2})(?![0-9])/i);
  if (loggerCut && String(loggerCut[1]).trim()) {
    site = loggerCut[1];
  } else {
    const clockCut = base.match(/^(.*?)[\s_]*\d{1,2}_\d{2}_\d{8,}/);
    if (clockCut && String(clockCut[1]).trim()) site = clockCut[1];
  }
  return fieldHighlightTokens(site).map(stripCameraIndex).filter((token) => token.length >= 3);
};

/** How many leading tokens are the Indus Informatics company prefix, if any. */
const companyPrefixLength = (tokens) => {
  if (tokens[0] === 'indusinformatics') return 1;
  if (tokens[0] === 'indus' && tokens[1] === 'informatics') return 2;
  return 0;
};

/**
 * Field Highlights belong to a bridge only when the filename's site name
 * starts with that bridge's first word.
 *
 * "Destin (East Pass) 9-41_...." matches Destin, not East zone: "east" is a
 * later word, not the site name. "IndusInformatics Theo Baar 1_00_...." and
 * "Indusinformatics Escambia 2_00_...." still match, because the company
 * prefix is skipped and the next word is the site.
 * A file with no site name is kept — this does not hide a clip we cannot read.
 */
export const fieldHighlightFileMatchesBridge = (fileName, bridgeName) => {
  const bridgeTokens = fieldHighlightTokens(bridgeName).map(stripCameraIndex).filter((token) => token.length >= 3);
  const bridgeToken = bridgeTokens[0] || '';
  if (!bridgeToken) return true;

  const siteTokens = fieldHighlightSiteTokens(fileName).filter((token) => /[a-z]/.test(token));
  if (!siteTokens.length) return true;

  const prefixLen = companyPrefixLength(siteTokens);
  const siteToken = siteTokens[prefixLen] || '';
  return siteToken === bridgeToken;
};
